// TODO: migrate from legacy CI tooling: resolve ts-* and/or eslint-* directives below (including inline comments)
/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps, react-hooks/immutability */

import _ from "lodash";
import React, { PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Checkbox, Popup } from "semantic-ui-react";
import "./panel.css";
import { AnyFilter, FilterOption } from "./types";
import { DatePicker } from "react-widgets/cjs";

interface MultiDropdownProps {
	label?: string;
	placeholder?: string;
	options: FilterOption[];
	defaultOptionIds?: string[];
	setFilters: (filter: (string | null)[], applied: boolean) => void;
	style?: React.CSSProperties;
	size?: "small" | "default";
	searchable?: boolean;
}

const SearchPanelMultiDropdown = ({
	label,
	placeholder = "Filters",
	options,
	defaultOptionIds = [],
	setFilters,
	style,
	size,
	searchable = true,
}: MultiDropdownProps) => {
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState("");
	const [optionsToShow, setOptionsToShow] = useState(options);
	const [checkedFilters, setCheckedFilters] = useState<{
		"checked-all": boolean;
		[key: string]: boolean;
	}>({
		"checked-all": true,
	});

	const checkedCount = useMemo(() => {
		if (checkedFilters["checked-all"]) {
			return -1; // negative one to signal that none should be shown
		}

		return _.countBy(checkedFilters).true ?? 0;
	}, [checkedFilters]);

	const allRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		const filteredValues = _.reduce(
			options,
			(acc, red) => {
				if (red.key !== "checked-all" && checkedFilters[red.key]) {
					acc.push(red.value);
				}
				return acc;
			},
			[] as (string | null)[],
		);

		const applied = !checkedFilters["checked-all"];

		setFilters(filteredValues, applied);
	}, [checkedFilters]);

	useEffect(() => {
		setOptionsToShow(options);
		setQuery("");

		if (defaultOptionIds.length) {
			if (allRef.current) {
				allRef.current.indeterminate = true;
			}

			setCheckedFilters({
				"checked-all": false,
				..._.reduce(
					options,
					(acc, red) =>
						defaultOptionIds.includes(red.key) ? { ...acc, [red.key]: true } : { ...acc, [red.key]: false },
					{},
				),
			});
		} else {
			if (allRef.current) {
				allRef.current.indeterminate = false;
			}

			setCheckedFilters({
				"checked-all": true,
				..._.reduce(options, (acc, red) => ({ ...acc, [red.key]: true }), {}),
			});
		}
	}, [options]);

	useEffect(() => {
		if (open) {
			setTimeout(() => {
				window.addEventListener("click", closeDropdown);
			});
		} else {
			window.removeEventListener("click", closeDropdown);
			setQuery("");
		}

		return () => {
			window.removeEventListener("click", closeDropdown);
		};
	}, [open]);

	useEffect(() => {
		searchOpts(query);
	}, [query]);

	const searchOpts = useCallback(
		(query: string) => {
			const re = RegExp(_.escapeRegExp(query), "i");
			setOptionsToShow(_.filter(options, (opt) => re.test(opt.text)));
		},
		[options],
	);

	const closeDropdown = useCallback(() => {
		setOpen(false);
	}, []);

	const handleCheck = useCallback(
		(key: keyof typeof checkedFilters, checked: boolean) => {
			if (key === "checked-all") {
				// reducer used to set everything to the checked value
				setCheckedFilters({
					"checked-all": checked,
					...options.reduce((acc, red) => ({ ...acc, [red.key]: checked }), {}),
				});
			} else {
				// reverse behavior when a user is searching for a column
				if (checkedFilters["checked-all"] && query.length > 0) {
					if (allRef.current) {
						allRef.current.indeterminate = true;
					}

					setCheckedFilters({
						"checked-all": false,
						...options.reduce((acc, red) => ({ ...acc, [red.key]: false }), {}),
						[key]: true,
					});
				} else {
					// check to see if all are checked
					// to do this we reset the "checked-all" value to true so it doesn't count in the reducer
					// don't need this to mutate/dispatch a setState yet so we make a clone
					const clonedChecked = _.cloneDeep(checkedFilters);
					clonedChecked["checked-all"] = true;
					clonedChecked[key] = checked;
					// check if all are true
					const allChecked = _.every(clonedChecked);
					clonedChecked["checked-all"] = allChecked;
					// check if select all should be indeterminate
					if (allRef.current) {
						if (!allChecked) {
							// case where there is at least one unchecked
							// check if there is at least one true
							if (_.some(clonedChecked)) {
								// case where at least one is checked
								allRef.current.indeterminate = true;
							} else {
								// case where none are selected
								allRef.current.indeterminate = false;
							}
						} else {
							// case where all are checked
							allRef.current.indeterminate = false;
						}
					}

					setCheckedFilters(clonedChecked);
				}
			}
		},
		[options, checkedFilters, allRef, query],
	);

	return (
		<div className="dropdownWrapper">
			{label && <label>{label}</label>}
			<div
				className={`panelInput${open ? " open" : ""}${size === "small" ? " small" : ""}`}
				onClick={() => setOpen((prev) => !prev)}
				style={style}
			>
				{(!open || !searchable) && (
					<span title={placeholder}>
						{placeholder}
						{checkedCount >= 0 ? ` (${checkedCount})` : ""}
					</span>
				)}
				{open && searchable && (
					<input
						title={placeholder}
						placeholder={size === "small" ? "Search..." : "Search Filters..."}
						className="dropdownInput"
						value={query}
						onChange={({ target }) => {
							setQuery(target.value);
						}}
						onClick={(e) => {
							e.stopPropagation();
						}}
						autoFocus
					/>
				)}
			</div>

			<div style={{ position: "relative" }}>
				<div
					className={`dropdownMenu${open ? " open" : ""}`}
					onClick={(e) => {
						e.stopPropagation();
					}}
				>
					<label key={"checked-all"}>
						<div className="dropdownItem">
							<input
								ref={allRef}
								type="checkbox"
								checked={checkedFilters["checked-all"]}
								onChange={({ target }) => {
									handleCheck("checked-all", target.checked);
								}}
							/>
							<span>Show All</span>
						</div>
					</label>
					{_.map(optionsToShow, (o) => (
						<label key={o.key}>
							<div className="dropdownItem" title={o.text}>
								<input
									type="checkbox"
									checked={checkedFilters[o.key] ?? false}
									onChange={({ target }) => {
										handleCheck(o.key, target.checked);
									}}
								/>
								{/* <div className="checkbox" /> */}
								<span>{o.text ?? "Not Set"}</span>
							</div>
						</label>
					))}
				</div>
			</div>
		</div>
	);
};

interface DropdownProps {
	placeholder?: string;
	options: FilterOption[];
	setSelection: (selection: string | null) => void;
	defaultOption: FilterOption;
	style?: React.CSSProperties;
	size?: "small" | "default";
	label?: string;
}

const SearchPanelDropdown = ({
	placeholder = "Filters",
	setSelection,
	defaultOption,
	options,
	style,
	size,
	label,
}: DropdownProps) => {
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState("");
	const [optionsToShow, setOptionsToShow] = useState(options);
	const [value, setValue] = useState<string | null>(defaultOption.value);
	const [text, setText] = useState<string | null>(defaultOption.text);

	useEffect(() => {
		setSelection(value);
	}, [value]);

	useEffect(() => {
		setOptionsToShow(options);
		setQuery("");
		setValue(defaultOption.value);
		setText(defaultOption.text);
	}, [options]);

	useEffect(() => {
		if (open) {
			setTimeout(() => {
				window.addEventListener("click", closeDropdown);
			});
		} else {
			window.removeEventListener("click", closeDropdown);
			setQuery("");
		}

		return () => {
			window.removeEventListener("click", closeDropdown);
		};
	}, [open]);

	useEffect(() => {
		searchOpts(query);
	}, [query]);

	const searchOpts = useCallback(
		(query: string) => {
			const re = RegExp(_.escapeRegExp(query), "i");
			setOptionsToShow(_.filter(options, (opt) => re.test(opt.text)));
		},
		[options],
	);

	const closeDropdown = useCallback(() => {
		setOpen(false);
	}, []);

	return (
		<div className="dropdownWrapper">
			{label && <label>{label}</label>}
			<div
				className={`panelInput${open ? " open" : ""}${size === "small" ? " small" : ""}`}
				onClick={() => setOpen((prev) => !prev)}
				style={style}
			>
				{!open && <span title={placeholder}>{text}</span>}
				{open && (
					<input
						title={placeholder}
						placeholder={size === "small" ? "Search..." : "Search Filters..."}
						className="dropdownInput"
						value={query}
						onChange={({ target }) => {
							setQuery(target.value);
						}}
						onClick={(e) => {
							e.stopPropagation();
						}}
						autoFocus
					/>
				)}
			</div>

			<div style={{ position: "relative" }}>
				<div
					className={`dropdownMenu${open ? " open" : ""}`}
					onClick={(e) => {
						e.stopPropagation();
					}}
				>
					{_.map(optionsToShow, (o) => (
						<div
							key={o.key}
							className="dropdownItem"
							title={o.text}
							onClick={() => {
								setValue(o.value);
								setText(o.text);
								setOpen(false);
							}}
						>
							<span>{o.text ?? "Not Set"}</span>
						</div>
					))}
				</div>
			</div>
		</div>
	);
};

interface SearchBarProps {
	// label?: string;
	placeholder?: string;
	searchTerm: string;
	setSearchTerm: (query: string) => void;
	style?: React.CSSProperties;
	size?: "small" | "default";
}

const SearchPanelBar = ({ size, style, placeholder, searchTerm, setSearchTerm }: SearchBarProps) => {
	return (
		<div className={`panelInput${size === "small" ? " small" : ""}`} style={{ marginRight: "0.25em", ...style }}>
			<input
				title={placeholder}
				placeholder={placeholder}
				className="dropdownInput"
				value={searchTerm}
				onChange={({ target }) => {
					setSearchTerm(target.value);
				}}
			/>
		</div>
	);
};

interface DatePickerProps {
	// presetOptions: FilterOption[];
	placeholder?: string;
	date: Date | null;
	setDate: React.Dispatch<React.SetStateAction<Date | null>>;
	label?: string;
}

const SearchPanelDatePicker = ({ date, setDate, placeholder, label }: DatePickerProps) => {
	// const [date, setDate] = useState<Date | null>(null);

	// useEffect(() => {
	// 	if (date) {
	// 		setFilters(date, true);
	// 	} else {
	// 		setFilters(date, false);
	// 	}
	// }, [date]);

	return (
		<div className="dropdownWrapper">
			{label && <label>{label}</label>}
			<DatePicker
				title={placeholder}
				placeholder={placeholder}
				includeTime={false}
				value={date}
				onChange={(date) => {
					if (date) {
						date.setHours(0, 0, 0, 0);
						setDate(date);
					} else {
						setDate(null);
					}
				}}
			/>
		</div>
	);
};

interface AdvancedPopupProps {
	filters: { [key: string]: AnyFilter };
	advancedHeaders: string[];
}

const SearchPanelAdvancedPopup = ({
	children,
	filters = {},
	advancedHeaders = [],
}: PropsWithChildren<AdvancedPopupProps>) => {
	const [open, setOpen] = useState(false);
	const numAppliedFilters = useMemo(
		() => advancedHeaders.reduce((acc, cur) => (filters[cur]?.applied ? acc + 1 : acc), 0),
		[filters, advancedHeaders],
	);

	useEffect(() => {
		if (open) {
			setTimeout(() => {
				window.addEventListener("click", closePopup);
			});
		} else {
			window.removeEventListener("click", closePopup);
		}

		return () => {
			window.removeEventListener("click", closePopup);
		};
	}, [open]);

	const closePopup = useCallback(() => {
		setOpen(false);
	}, []);

	return (
		<div style={{ position: "relative" }}>
			<Button color="blue" onClick={() => setOpen((prev) => !prev)}>
				More Filters{numAppliedFilters > 0 ? ` (${numAppliedFilters})` : ""}
			</Button>
			<div className={`panelPopup${open ? " open" : ""}`} onClick={(e) => e.stopPropagation()}>
				<div className="header">More Filters</div>
				<div className="content">{children}</div>
			</div>
		</div>
	);
};

interface CheckboxProps {
	label: string;
	checked: boolean;
	setChecked: React.Dispatch<React.SetStateAction<boolean>>;
}

const SearchPanelCheckbox = ({ label, checked, setChecked }: CheckboxProps) => {
	return (
		<label className="checkboxContainer">
			<input
				type="checkbox"
				checked={checked}
				onChange={({ target }) => {
					setChecked(target.checked);
				}}
			/>
			<span>{label}</span>
		</label>
	);
};

interface PanelProps {
	onReset?: () => void;
}

const SearchPanel = ({ onReset, children }: PropsWithChildren<PanelProps>) => {
	return (
		<div className="panelBackground">
			{children}
			{onReset && (
				<Button
					style={{ margin: 0 }}
					negative
					onClick={() => {
						onReset();
					}}
				>
					Reset
				</Button>
			)}
		</div>
	);
};

// SearchPanel.Title = SearchPanelTitle;
SearchPanel.MultiDropdown = SearchPanelMultiDropdown;
SearchPanel.Dropdown = SearchPanelDropdown;
SearchPanel.Bar = SearchPanelBar;
SearchPanel.Date = SearchPanelDatePicker;
SearchPanel.Checkbox = SearchPanelCheckbox;
SearchPanel.AdvancedPopup = SearchPanelAdvancedPopup;

export default SearchPanel;
